/**
 * DataTable.tsx — tabell med kolonner, rader og sortering
 *
 * Ren React-komponent stylet med Carbon design-tokens.
 * Generisk komponent som tar kolonne-definisjoner og rad-data.
 *
 * Bruk:
 *   const columns = [
 *     { key: 'name', header: 'Navn', sortable: true },
 *     { key: 'email', header: 'Epost', sortable: false },
 *   ];
 *   <DataTable columns={columns} rows={users} onRowClick={handleClick} />
 */

import { ReactNode } from 'react';
import styles from './data-table.module.css';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  // Render-funksjon for tilpasset innhold per celle
  render?: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  // Tabellnivå (bulk) utvalg
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /**
   * Felt-navn som brukes som unik rad-nøkkel (default 'id').
   * Kreves hvis radene ikke har et 'id'-felt (f.eks. view-rader med
   * tuning_job_id). Kreves også ved selectable.
   */
  rowKey?: keyof T & string;
}

export default function DataTable<T>({
  columns,
  rows,
  sortKey,
  sortDirection,
  onSort,
  onRowClick,
  emptyMessage = 'Ingen data tilgjengelig',
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  rowKey,
}: DataTableProps<T>) {
  const handleSort = (col: Column<T>) => {
    if (col.sortable && onSort) {
      onSort(col.key);
    }
  };

  // Hent unik nøkkel fra en rad (rowKey-felt eller 'id' som default)
  const getRowId = (row: T): string => {
    const key = rowKey ?? 'id';
    const value = (row as Record<string, unknown>)[key];
    return value === undefined || value === null ? '' : String(value);
  };

  // Velg/fravelg alle rader på gjeldende side
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(getRowId(r)));
  const someSelected = rows.some((r) => selectedIds.includes(getRowId(r)));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(selectedIds.filter((id) => !rows.some((r) => getRowId(r) === id)));
    } else {
      const merged = new Set(selectedIds);
      rows.forEach((r) => merged.add(getRowId(r)));
      onSelectionChange([...merged]);
    }
  };

  const toggleRow = (id: string) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };
  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {selectable && (
              <th className={styles.thCheckbox}>
                <input
                  type="checkbox"
                  aria-label="Velg alle"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allSelected && someSelected;
                  }}
                  onChange={toggleAll}
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className={col.sortable ? styles.thSortable : styles.th}
                onClick={() => handleSort(col)}
                aria-sort={
                  sortKey === col.key
                    ? sortDirection === 'asc' ? 'ascending' : 'descending'
                    : undefined
                }
              >
                <span className={styles.thContent}>
                  {col.header}
                  {col.sortable && sortKey === col.key && (
                    <span className={styles.sortIndicator}>
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.emptyCell}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={getRowId(row)}
                className={onRowClick ? styles.rowClickable : styles.row}
                onClick={() => onRowClick?.(row)}
                // Carbon a11y: klikkbare rader er fokuserbare og aktivérbare med Enter
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {selectable && (
                  <td className={styles.tdCheckbox} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Velg ${getRowId(row)}`}
                      checked={selectedIds.includes(getRowId(row))}
                      onChange={() => toggleRow(getRowId(row))}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className={styles.td}>
                    {col.render ? col.render(row) : (row as Record<string, ReactNode>)[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
